import React, { useState, useMemo, useRef, useCallback } from 'react';
import cx from 'classnames';
import { Form, FormGroup, Button } from 'reactstrap';
import ProductManagerCombobox from './ProductManagerCombobox';
import styles from './ProductManagerForm.module.scss';
import ProductManagerSearchResult from './ProductManagerSearchResult';

const ProductManagerForm = ({
  onSubmit,
  onCancel,
  onSearch,
  userGroup = { product_managers: [] },
}) => {
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const searchIdRef = useRef(0);
  const debounceTimeoutRef = useRef(null);

  const existingPMs = useMemo(
    () => userGroup?.product_managers || [],
    [userGroup]
  );

  const handleSearch = useCallback(
    async (query) => {
      if (!query) {
        setSearchResults([]);
        setSelectedUser(null);
        return;
      }

      // Increment search ID to track this request
      const currentSearchId = ++searchIdRef.current;
      setIsSearching(true);

      try {
        const results = await onSearch(query);

        // Only update results if this is still the current search
        if (currentSearchId === searchIdRef.current) {
          setSearchResults(
            results.filter(
              (r) => !existingPMs.some((pm) => pm.user_id === r.id)
            )
          );
        }
      } catch (error) {
        console.error('Search failed:', error);
        // Only update results if this is still the current search
        if (currentSearchId === searchIdRef.current) {
          setSearchResults([]);
        }
      } finally {
        // Only update loading state if this is still the current search
        if (currentSearchId === searchIdRef.current) {
          setIsSearching(false);
        }
      }
    },
    [onSearch, existingPMs]
  );

  const debouncedSearch = useCallback(
    (query) => {
      // Clear any existing timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // Set a new timeout for the search
      debounceTimeoutRef.current = setTimeout(() => {
        handleSearch(query);
      }, 100); // 300ms debounce delay
    },
    [handleSearch]
  );

  const handleReset = useCallback(() => {
    setInputValue('');
    setSelectedUser(null);
    // Clear any pending search timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    // Increment search ID to invalidate any pending searches
    searchIdRef.current++;
    onCancel();
  }, [onCancel]);

  const handleSelectUser = useCallback(
    async (selected) => {
      // const selected = searchResults.find((user) => user.name === name);
      if (selected) {
        setSelectedUser(selected);

        setIsSubmitting(true);
        try {
          await onSubmit({ user_id: selected.id });
          handleReset();
        } finally {
          setIsSubmitting(false);
        }
      }
    },
    [onSubmit, handleReset]
  );

  const handleInputChange = (value) => {
    setInputValue(value);
    debouncedSearch(value);
  };

  const handleAddUser = async () => {
    if (selectedUser) {
      setIsSubmitting(true);
      try {
        await onSubmit({ user_id: selectedUser.id });
        handleReset();
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const isSelectedUserAlreadyAdded =
    selectedUser &&
    (userGroup?.product_managers || []).some(
      (existing) => existing.user?.id === selectedUser.id
    );

  return (
    <Form className={styles['product-manager-form']}>
      <div className={styles['form-body']}>
        <div className={styles['product-manager-search']}>
          <FormGroup>
            <ProductManagerCombobox
              placeholder="Search for product managers..."
              onChange={handleInputChange}
              onItemClick={handleSelectUser}
              value={inputValue}
              list={searchResults}
              itemToKey={(u) => u.id}
              itemToString={(u) => u.display_name || u.username || ''}
              itemComponent={ProductManagerSearchResult}
            />
            {!isSearching && inputValue && selectedUser && (
              <div
                className={cx(styles.feedback, {
                  'text-success': !isSelectedUserAlreadyAdded,
                  'text-danger': isSelectedUserAlreadyAdded,
                })}
              >
                {isSelectedUserAlreadyAdded
                  ? 'This user is already a product manager in this user group'
                  : 'User selected'}
              </div>
            )}
          </FormGroup>
        </div>

        <div className={styles['product-manager-actions']}>
          {selectedUser && !isSelectedUserAlreadyAdded ? (
            <Button
              color="primary"
              disabled={isSubmitting}
              onClick={handleAddUser}
            >
              Add Product Manager
            </Button>
          ) : null}
          <Button
            color="secondary"
            disabled={isSubmitting}
            onClick={handleReset}
          >
            Cancel
          </Button>
        </div>
      </div>
    </Form>
  );
};

export default ProductManagerForm;
